import axios from "axios";
import AuthenticationFailureHandler from "../utils/AuthenticationFailureHandler";
import { is401 } from "../errors/ErrorConstants";
import { ONBOARDING_BASE_URL } from "../utils/EmpUtil";

/**
 * @typedef {Object} DealerInfoOnboardingPayload
 * @property {string} dealerCommonId
 * @property {string} dealerName
 * @property {string} brand
 * @property {boolean} dualDealer
 * @property {string} market
 * @property {string} [paCode]
 * @property {string} [salesCode]
 * @property {string} [dealerType]
 * @property {string} [addressStreet1]
 * @property {string} [addressCity]
 * @property {string} [addressState]
 * @property {string} [addressZip]
 * @property {string} [userId] - Logged-in user id for dealer_onboarding.updated_by
 */

export default class DealerOnboardingClient {
  static baseUrl = ONBOARDING_BASE_URL;

  /**
   * Get current dealer onboarding progress.
   * @param {string} dealerCommonId - Dealer common ID
   * @param {DealerInfoOnboardingPayload} dealerInfo - DealerInfoDto payload (may include address from dealer group)
   * @returns {Promise<DealerOnboardingProgressResponse>}
   */
  static getDealerOnboardingProgress(dealerCommonId, dealerInfo) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/onboarding/progress?dealerCommonId=${encodedDealerCommonId}`;
    return axios.post(url, dealerInfo || {});
  }

  /**
   * Record Enrollment Terms & Conditions acceptance. Server resolves the substep and audit payload.
   * @param {string} dealerCommonId
   * @param {{ updatedBy: string }} body
   * @returns {Promise<void>}
   */
  static async acceptEnrollmentTerms(dealerCommonId, body) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/onboarding/accept-enrollment-terms?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, body);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Generic substep progress save (stepId/substepId from progress API). For Enrollment terms
   * acceptance, use `acceptEnrollmentTerms` instead.
   * @param {string} dealerCommonId
   * @param {{ stepId: number, substepId: number, status?: string, updatedBy: string, payloadJson?: object }} body - SubstepSaveRequest
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  static saveSubstep(dealerCommonId, body) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/onboarding/substep-save?dealerCommonId=${encodedDealerCommonId}`;
    return axios.post(url, body);
  }

  static async updateDealerPhoneNumber(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/onboarding/phone-number?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  static getTeamMembers(dealerCommonId) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/team-members?dealerCommonId=${encodedDealerCommonId}`;

    return axios
      .get(url)
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static addTeamMember(dealerCommonId, payload, userId) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/team-members/add?dealerCommonId=${encodedDealerCommonId}`;

    return axios
      .post(url, {
        ...payload,
        userId,
      })
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static updateTeamMember(dealerCommonId, memberId, payload, userId) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/team-members/${memberId}?dealerCommonId=${encodedDealerCommonId}`;

    return axios
      .put(url, {
        ...payload,
        userId,
      })
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static deleteTeamMember(dealerCommonId, memberId) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/team-members/${memberId}?dealerCommonId=${encodedDealerCommonId}`;
    return axios
      .delete(url)
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static async saveVehiclePricingPreference(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/vehicle-pricing-preference?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Fetch RouteOne partner dealers for a given PA code.
   * @param {string} paCode - Ford PA code (maps to dmsDealerNumber in RouteOne)
   * @returns {Promise<Array<{routeOneDealerId: string, dealerLegalName: string}>>}
   */
  static async getRouteOneDealers(paCode) {
    const encodedPaCode = encodeURIComponent(paCode);
    const url = `${this.baseUrl}/routeone/partner-dealers?paCode=${encodedPaCode}`;

    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Save the selected RouteOne Dealer ID for a dealer.
   * @param {string} dealerCommonId
   * @param {{ routeOneId: string, updatedBy: string }} payload
   * @returns {Promise<any>}
   */
  static async saveRouteOneDealerId(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/route-one-dealer-id?dealerCommonId=${encodedDealerCommonId}`;

    try {
      const response = await axios.post(url, payload);
      return response.data;
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  static async saveTierThreeBuyNowPreference(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/buy-now-preference?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Save trade-in valuation preference for a dealer.
   * @param {string} dealerCommonId
   * @param {{ tradeInPreference: "LINCOLN_BACKED_VALUE"|"ESTIMATED_VALUE", updatedBy: string }} payload
   * @returns {Promise<void>}
   */
  static async saveTradeInPreference(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/trade-in-preference?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Save Stripe payment-processor preference for a dealer.
   * @param {string} dealerCommonId
   * @param {{ stripePreference: "Yes"|"No", updatedBy: string }} payload
   * @returns {Promise<void>}
   */
  static async saveStripePreference(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/stripe-preference?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Save advertised plan pricing preference for a dealer.
   * @param {string} dealerCommonId
   * @param {{ advertisedPlanPricingPreference: string, updatedBy: string }} payload
   * @returns {Promise<void>}
   */
  static async saveAdvertisedPlanPricingPreference(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/advertised-plan-pricing-preference?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Save third-party financing enrollment for a dealer.
   * @param {string} dealerCommonId
   * @param {{ thirdPartyFinancingPreference: string, updatedBy: string }} payload
   * @returns {Promise<void>}
   */
  static async saveThirdPartyFinancingEnrollment(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/configuration/third-party-financing-enrollment?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }
}
